// src/components/admin/StoreCustomizerDrawer.tsx
"use client";

import { useState, useEffect, useCallback, useMemo } from "react";
import Swal from "sweetalert2";
import {
  Settings,
  X,
  Sparkles,
  Sliders,
  Image as ImageIcon,
  Flame,
  Star,
  Award,
  BookOpen,
  HelpCircle,
  MessageSquareQuote,
  Images,
  Mail,
  CheckCircle2,
  RefreshCw,
  Loader2,
  Store,
  Building2,
} from "lucide-react";

interface StoreCustomizerDrawerProps {
  subdomain: string;
  theme?: "dark" | "light";
  primaryColor?: string;
  isOpen?: boolean;
  onClose?: () => void;
}

interface SectionVisibilityState {
  // 1. Promotional Banners
  hero_banner: boolean;
  countdown_banner: boolean;
  wide_banner: boolean;
  // 2. AI Recommender
  ai_recommender: boolean;
  // 3. Brand Shops & Branch Outlets (🌟 Notun Section Gulo)
  brand_shops: boolean;
  branch_stores: boolean;
  // 4. Products & Showcases
  featured_products: boolean;
  deals_of_the_day: boolean;
  best_sellers: boolean;
  // 5. Content, Trust & Engagement
  blog_posts: boolean;
  faqs: boolean;
  testimonials: boolean;
  gallery: boolean;
  newsletter: boolean;
}

const DEFAULT_SECTIONS: SectionVisibilityState = {
  hero_banner: true,
  countdown_banner: true,
  wide_banner: true,
  ai_recommender: true,
  brand_shops: true,
  branch_stores: true,
  featured_products: true,
  deals_of_the_day: true,
  best_sellers: true,
  blog_posts: true,
  faqs: true,
  testimonials: true,
  gallery: true,
  newsletter: true,
};

export default function StoreCustomizerDrawer({
  subdomain,
  theme: propTheme,
  primaryColor = "#059669",
  isOpen: externalIsOpen,
  onClose: externalOnClose,
}: StoreCustomizerDrawerProps) {
  // 🌟 Auto-Theme Detection
  const [internalTheme, setInternalTheme] = useState<"dark" | "light">(propTheme || "dark");

  useEffect(() => {
    if (propTheme) {
      setInternalTheme(propTheme);
      return;
    }

    const checkTheme = () => {
      if (typeof window === "undefined") return;
      const isDocLight =
        document.documentElement.classList.contains("light") ||
        localStorage.getItem("admin_theme") === "light";
      setInternalTheme(isDocLight ? "light" : "dark");
    };

    checkTheme();
    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    window.addEventListener("storage", checkTheme);

    return () => {
      observer.disconnect();
      window.removeEventListener("storage", checkTheme);
    };
  }, [propTheme]);

  const isLight = (propTheme || internalTheme) === "light";

  // Internal Open/Close State
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;

  const handleClose = () => {
    if (externalOnClose) externalOnClose();
    else setInternalIsOpen(false);
  };

  const [visibility, setVisibility] = useState<SectionVisibilityState>(DEFAULT_SECTIONS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const getApiUrl = useCallback(
    (endpoint: string) => {
      const isLocal =
        typeof window !== "undefined" &&
        (window.location.hostname.includes("localhost") ||
          window.location.hostname === "127.0.0.1");
      return isLocal ? `http://${subdomain}.localhost:8000${endpoint}` : endpoint;
    },
    [subdomain]
  );

  const getAuthToken = () =>
    localStorage.getItem("merchant_token") || localStorage.getItem("token") || "";

  // 🌟 Settings & Section State Fetch
  const fetchSettings = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiUrl(`/api/v1/stores/settings/?subdomain=${subdomain}`), {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        if (data.section_visibility && typeof data.section_visibility === "object") {
          setVisibility({
            ...DEFAULT_SECTIONS,
            ...data.section_visibility,
          });
        }
      }
    } catch (err) {
      console.error("Error fetching section visibility:", err);
    } finally {
      setLoading(false);
    }
  }, [getApiUrl, subdomain]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  // Toggle Handler
  const handleToggle = (key: keyof SectionVisibilityState) => {
    setVisibility((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  // Enable/Disable All Handler
  const handleToggleAll = (status: boolean) => {
    const updated = Object.keys(visibility).reduce((acc, curr) => {
      acc[curr as keyof SectionVisibilityState] = status;
      return acc;
    }, {} as SectionVisibilityState);
    setVisibility(updated);
  };

  // 🌟 Save & Persist to Server
  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(getApiUrl("/api/v1/stores/settings/"), {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${getAuthToken()}`,
        },
        body: JSON.stringify({
          section_visibility: visibility,
        }),
      });

      if (res.ok) {
        Swal.fire({
          icon: "success",
          title: "সেকশন সেটিংস সংরক্ষিত!",
          text: "মেইন স্টোরফ্রন্টে সেকশনগুলোর ভিজিবিলিটি আপডেট হয়েছে।",
          toast: true,
          position: "top-end",
          timer: 2000,
          showConfirmButton: false,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: "সংরক্ষণ ব্যর্থ!",
          text: "সেটিংস আপডেট করা সম্ভব হয়নি।",
        });
      }
    } catch {
      Swal.fire({
        icon: "error",
        title: "সার্ভার এরর!",
        text: "সার্ভারের সাথে সংযোগ স্থাপন করা যায়নি।",
      });
    } finally {
      setSaving(false);
    }
  };

  const sectionsList: Array<{
    group: string;
    items: Array<{
      key: keyof SectionVisibilityState;
      title: string;
      desc: string;
      icon: any;
    }>;
  }> = useMemo(
    () => [
      {
        group: "1. Promotional Banners (৩টি ব্যানার)",
        items: [
          {
            key: "hero_banner",
            title: "Hero Banner Slider",
            desc: "মূল হোমপেজের টপ ব্যানার স্লাইডার",
            icon: ImageIcon,
          },
          {
            key: "countdown_banner",
            title: "Countdown & Dual Promo",
            desc: "কাউন্টডাউন টাইমার ও ডুয়েল অফার ব্যানার",
            icon: Sparkles,
          },
          {
            key: "wide_banner",
            title: "Wide Deal / Discount Banner",
            desc: "হোমপেজের ওয়াইড ডিল ও ডিসকাউন্ট ব্যানার",
            icon: Sliders,
          },
        ],
      },
      {
        group: "2. AI Content & Recommendations",
        items: [
          {
            key: "ai_recommender",
            title: "AI Product Recommender",
            desc: "হাইব্রিড কন্টেন্ট ও ট্রেন্ডিং এআই প্রোডাক্ট ফিড",
            icon: Sparkles,
          },
        ],
      },
      {
        group: "3. Brand Shops & Physical Outlets (🌟 নতুন আউটলেট ও ব্রাঞ্চ)",
        items: [
          {
            key: "brand_shops",
            title: "Brand Shops & Outlets",
            desc: "হোমপেজের মাল্টি-শপ কার্ড ও ব্র্যান্ড আউটলেট সেকশন",
            icon: Store,
          },
          {
            key: "branch_stores",
            title: "Branch Stores & Outlets",
            desc: "শাখা দোকান ও ফিজিক্যাল আউটলেট ভিজিট লিঙ্ক সেকশন",
            icon: Building2,
          },
        ],
      },
      {
        group: "4. Product Catalogs & Showcases",
        items: [
          {
            key: "featured_products",
            title: "Featured Products",
            desc: "নির্বাচিত বিশেষ পণ্যের সেকশন",
            icon: Star,
          },
          {
            key: "deals_of_the_day",
            title: "Deals of the Day",
            desc: "প্রতিদিনের আকর্ষণীয় ডিল ও ছাড়",
            icon: Flame,
          },
          {
            key: "best_sellers",
            title: "Best Seller Products",
            desc: "সর্বোচ্চ বিক্রিত পণ্যের তালিকা",
            icon: Award,
          },
        ],
      },
      {
        group: "5. Store Content, Trust & Engagement",
        items: [
          {
            key: "blog_posts",
            title: "Store Blogs & Articles",
            desc: "দোকানের নিউজ ও ব্লগ আর্টিকেল ফিড",
            icon: BookOpen,
          },
          {
            key: "faqs",
            title: "Customer FAQs",
            desc: "সচরাচর জিজ্ঞাসিত প্রশ্ন ও উত্তর",
            icon: HelpCircle,
          },
          {
            key: "testimonials",
            title: "Customer Testimonials",
            desc: "গ্রাহকদের রিভিউ ও প্রশংসামূলক মন্তব্য",
            icon: MessageSquareQuote,
          },
          {
            key: "gallery",
            title: "Photo & Media Gallery",
            desc: "পণ্য ও স্টোরফ্রন্ট ফটো গ্যালারি",
            icon: Images,
          },
          {
            key: "newsletter",
            title: "Newsletter Subscription",
            desc: "ইমেইল নিউজলেটার সাবস্ক্রিপশন বক্স",
            icon: Mail,
          },
        ],
      },
    ],
    []
  );

  const activeCount = Object.values(visibility).filter(Boolean).length;
  const totalCount = Object.keys(visibility).length;

  return (
    <>
      {/* 🌟 ফ্লোটিং ট্রিগার বাটন */}
      {externalIsOpen === undefined && (
        <button
          type="button"
          onClick={() => setInternalIsOpen(true)}
          style={{ backgroundColor: primaryColor }}
          className="fixed right-0 top-1/2 -translate-y-1/2 z-[9990] p-3 rounded-l-2xl text-white shadow-2xl hover:pr-4 transition-all flex items-center gap-2 cursor-pointer group active:scale-95"
          title="Storefront Sections & Theme Settings"
        >
          <Settings className="w-5 h-5 animate-[spin_6s_linear_infinite] group-hover:rotate-90 transition-transform" />
          <span className="text-[11px] font-black uppercase tracking-wider hidden sm:inline-block pr-1">
            Sections ({activeCount}/{totalCount})
          </span>
        </button>
      )}

      {/* ব্যাকড্রপ ওভারলে */}
      {isOpen && (
        <div
          onClick={handleClose}
          className="fixed inset-0 bg-slate-950/60 backdrop-blur-xs z-[9998] transition-opacity animate-in fade-in"
        />
      )}

      {/* 🌟 স্লাইড-আউট ড্রয়ার প্যানেল */}
      <div
        className={`fixed inset-y-0 right-0 z-[9999] w-full max-w-md h-full flex flex-col justify-between shadow-2xl transition-transform duration-300 ease-in-out border-l-2 ${
          isOpen ? "translate-x-0" : "translate-x-full"
        } ${
          isLight
            ? "bg-white border-slate-300 text-slate-900"
            : "bg-slate-900 border-slate-800 text-white"
        }`}
      >
        {/* ড্রয়ার হেডার */}
        <div
          className={`p-5 border-b-2 flex items-center justify-between shrink-0 ${
            isLight ? "bg-slate-50 border-slate-200" : "bg-slate-950 border-slate-800"
          }`}
        >
          <div className="flex items-center gap-3">
            <div
              style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}
              className="w-10 h-10 rounded-2xl flex items-center justify-center border font-bold"
            >
              <Sliders className="w-5 h-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-base font-black tracking-tight">Theme & Section Settings</h3>
                <span className="px-2 py-0.5 rounded-full text-[9px] font-black uppercase bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 font-mono">
                  Live Sync
                </span>
              </div>
              <p className={`text-xs ${isLight ? "text-slate-500" : "text-slate-400"}`}>
                মেইন স্টোরফ্রন্টে কোন সেকশন অন বা অফ থাকবে তা নিয়ন্ত্রণ করুন
              </p>
            </div>
          </div>

          <button
            onClick={handleClose}
            className={`p-2 rounded-xl border transition-colors cursor-pointer ${
              isLight
                ? "bg-white border-slate-300 text-slate-600 hover:bg-slate-100"
                : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
            }`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* ড্রয়ার কন্ট্রোলস বার */}
        <div
          className={`px-5 py-3 border-b flex items-center justify-between text-xs font-bold ${
            isLight ? "bg-white border-slate-200" : "bg-slate-900 border-slate-800"
          }`}
        >
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-mono font-black text-emerald-600">
              {activeCount} Active
            </span>
            <span className="text-slate-400">/</span>
            <span className="text-[11px] font-mono text-slate-400">{totalCount} Total</span>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => handleToggleAll(true)}
              className="text-[11px] font-black text-emerald-600 hover:underline cursor-pointer"
            >
              Enable All
            </button>
            <span className="text-slate-300 dark:text-slate-700">|</span>
            <button
              onClick={() => handleToggleAll(false)}
              className="text-[11px] font-black text-rose-500 hover:underline cursor-pointer"
            >
              Disable All
            </button>
          </div>
        </div>

        {/* ড্রয়ার স্ক্রলযোগ্য তালিকা */}
        <div className="flex-1 overflow-y-auto p-5 space-y-6 [scrollbar-width:thin]">
          {loading ? (
            <div className="py-24 text-center space-y-3">
              <Loader2 style={{ color: primaryColor }} className="w-7 h-7 animate-spin mx-auto" />
              <p className="text-xs font-bold text-slate-400 font-mono">Loading sections...</p>
            </div>
          ) : (
            sectionsList.map((group, gIdx) => (
              <div key={gIdx} className="space-y-3">
                <span
                  className={`text-[10px] font-black uppercase tracking-wider block ${
                    isLight ? "text-slate-500" : "text-slate-400"
                  }`}
                >
                  {group.group}
                </span>

                <div className="space-y-2.5">
                  {group.items.map((sec) => {
                    const Icon = sec.icon;
                    const isEnabled = visibility[sec.key];

                    return (
                      <div
                        key={sec.key}
                        onClick={() => handleToggle(sec.key)}
                        className={`p-3.5 rounded-2xl border-2 transition-all flex items-center justify-between gap-3 cursor-pointer select-none ${
                          isEnabled
                            ? isLight
                              ? "bg-emerald-50/50 border-emerald-300 shadow-2xs"
                              : "bg-emerald-950/20 border-emerald-500/30"
                            : isLight
                            ? "bg-slate-50 border-slate-200 opacity-60 hover:opacity-100"
                            : "bg-slate-950/50 border-slate-800 opacity-60 hover:opacity-100"
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <div
                            className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border ${
                              isEnabled
                                ? "bg-emerald-500 text-white border-emerald-600 shadow-xs"
                                : isLight
                                ? "bg-slate-200 text-slate-600 border-slate-300"
                                : "bg-slate-800 text-slate-400 border-slate-700"
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="min-w-0">
                            <h4
                              className={`text-xs font-black truncate ${
                                isLight ? "text-slate-950" : "text-white"
                              }`}
                            >
                              {sec.title}
                            </h4>
                            <p className="text-[10px] text-slate-400 truncate">{sec.desc}</p>
                          </div>
                        </div>

                        {/* কাস্টম অন/অফ টগল সুইচ */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle(sec.key);
                          }}
                          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out outline-none ${
                            isEnabled ? "bg-emerald-600" : isLight ? "bg-slate-300" : "bg-slate-700"
                          }`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                              isEnabled ? "translate-x-5" : "translate-x-0"
                            }`}
                          />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* ড্রয়ার ফুটার অ্যাকশনস */}
        <div
          className={`p-5 border-t-2 shrink-0 flex items-center gap-3 ${
            isLight ? "bg-slate-50 border-slate-200" : "bg-slate-950 border-slate-800"
          }`}
        >
          <button
            type="button"
            onClick={fetchSettings}
            disabled={saving || loading}
            className={`w-1/3 py-2.5 rounded-xl border-2 text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
              isLight
                ? "bg-white border-slate-300 text-slate-700 hover:bg-slate-100"
                : "bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700"
            }`}
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            <span>Reset</span>
          </button>

          <button
            type="button"
            onClick={handleSave}
            disabled={saving || loading}
            style={{ backgroundColor: primaryColor }}
            className="w-2/3 py-2.5 rounded-xl text-white text-xs font-black shadow-lg shadow-emerald-600/25 hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}
            <span>{saving ? "Saving..." : "Save & Apply"}</span>
          </button>
        </div>
      </div>
    </>
  );
}